import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  createUser,
  deleteUser,
  getCurrentSession,
  getUsers,
  logout,
  refreshSession,
  updateUser,
} from "./utils/userManager";

const Page = styled.div`
  width: 100vw;
  min-height: 100vh;
  background: #f2f5f8;
  color: #2a3142;
  padding: 2vh 2vw;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2vh;
`;

const Title = styled.h1`
  color: #2a3142;
`;

const Button = styled.button`
  border: 2px solid #4a6283;
  color: #4a6283;
  background: white;
  padding: 0.8vh 1.2vw;
  font-size: 1.8vh;
  cursor: pointer;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1.5vh;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  padding: 1.2vh 1vw;
  border: 1px solid #d5dee8;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8vh 0.5vw;
  margin-bottom: 0.8vh;
  border: 1px solid #bcc9d8;
`;

const UserRow = styled.button`
  width: 100%;
  text-align: left;
  padding: 0.9vh 0.7vw;
  border: 1px solid ${(props) => (props.selected ? "#4a6283" : "#d5dee8")};
  background: ${(props) => (props.selected ? "#f0f5ff" : "white")};
  color: #2a3142;
  cursor: pointer;
  margin-bottom: 0.6vh;
`;

const FacilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.8vh;
  margin-top: 1vh;
`;

const FacilityItem = styled.label`
  display: flex;
  gap: 0.6vh;
  align-items: center;
  border: 1px solid #d5dee8;
  padding: 0.8vh 0.6vw;
`;

const Note = styled.p`
  color: #5f6f84;
  font-size: 1.6vh;
  margin-top: 0.6vh;
`;

const AdminSettings = () => {
  const [session, setSession] = useState(() => getCurrentSession());
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [newName, setNewName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(true);
  const [showResetPassword, setShowResetPassword] = useState(true);
  const [newAdmin, setNewAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const selectedUser = useMemo(
    () => users.find((user) => user.id === selectedId),
    [selectedId, users]
  );

  async function loadUsersAndFacilities() {
    const [usersFromApi, facilityResponse] = await Promise.all([
      getUsers(),
      fetch("https://server.edab.online/getFacilityNames"),
    ]);

    const facilityNames = await facilityResponse.json();
    setUsers(usersFromApi);
    setFacilities(Array.isArray(facilityNames) ? facilityNames : []);

    if (usersFromApi.length > 0) {
      setSelectedId((prev) => prev || usersFromApi[0].id);
    } else {
      setSelectedId(null);
    }
  }

  useEffect(() => {
    const init = async () => {
      try {
        const freshSession = await refreshSession();
        if (!freshSession || !freshSession.isAdmin) {
          navigate("/");
          return;
        }

        setSession(freshSession);
        await loadUsersAndFacilities();
      } catch (error) {
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [navigate]);

  async function addUser() {
    const name = newName.trim();

    if (!name || !newPassword) {
      alert("Name and password are required.");
      return;
    }

    if (newPassword.length < 8) {
      alert("Password must be at least 8 characters.");
      return;
    }

    try {
      const created = await createUser({
        name,
        password: newPassword,
        isAdmin: newAdmin,
        canRefreshService: newAdmin,
        facilities: [],
      });

      setUsers((prev) => [...prev, created]);
      setSelectedId(created.id);
      setNewName("");
      setNewPassword("");
      setNewAdmin(false);
      await loadUsersAndFacilities();
    } catch (error) {
      alert(error.message || "Failed to create user.");
    }
  }

  async function persistSelectedUser(changes) {
    if (!selectedUser) return;

    try {
      await updateUser({ id: selectedUser.id, ...changes });
      await loadUsersAndFacilities();
    } catch (error) {
      alert(error.message || "Failed to update user.");
    }
  }

  async function toggleFacility(facility) {
    if (!selectedUser || selectedUser.isAdmin) return;

    const hasFacility = selectedUser.facilities.includes(facility);
    const facilitiesForUser = hasFacility
      ? selectedUser.facilities.filter((name) => name !== facility)
      : [...selectedUser.facilities, facility];

    await persistSelectedUser({ facilities: facilitiesForUser });
  }

  async function removeSelectedUser() {
    if (!selectedUser) return;

    try {
      await deleteUser(selectedUser.id);
      await loadUsersAndFacilities();
    } catch (error) {
      alert(error.message || "Failed to delete user.");
    }
  }

  function canDemoteSelectedAdmin() {
    if (!selectedUser?.isAdmin) return true;
    if (selectedUser.id === session?.id) return false;
    return users.filter((user) => user.isAdmin).length > 1;
  }

  if (loading || !session || !session.isAdmin) {
    return null;
  }

  return (
    <Page>
      <Header>
        <Title>Admin Settings</Title>
        <div style={{ display: "flex", gap: "0.8vw" }}>
          <Button onClick={() => navigate("/edabstatus")}>Back to status</Button>
          <Button
            onClick={async () => {
              await logout();
              navigate("/");
            }}
          >
            Log out
          </Button>
        </div>
      </Header>

      <Grid>
        <Card>
          <h2>Users</h2>
          {users.map((user) => (
            <UserRow
              key={user.id}
              selected={user.id === selectedId}
              onClick={() => setSelectedId(user.id)}
            >
              {user.name} {user.isAdmin ? "Admin" : "User"}
            </UserRow>
          ))}
        </Card>

        <Card>
          <h2>Add user</h2>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Name (example: Night Shift)"
          />
          <Input
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Password (min 8 chars)"
          />
          <label style={{ display: "flex", alignItems: "center", gap: "0.6vh" }}>
            <input
              type="checkbox"
              checked={showNewPassword}
              onChange={(e) => setShowNewPassword(e.target.checked)}
            />
            Show password
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.6vh" }}>
            <input
              type="checkbox"
              checked={newAdmin}
              onChange={(e) => setNewAdmin(e.target.checked)}
            />
            Admin user
          </label>
          <div style={{ marginTop: "1vh" }}>
            <Button onClick={addUser}>Create user</Button>
          </div>
        </Card>
      </Grid>

      {selectedUser && (
        <Card style={{ marginTop: "1.5vh" }}>
          <h2>Edit user</h2>
          <Input
            value={selectedUser.name}
            onChange={(e) => {
              setUsers((prev) =>
                prev.map((user) =>
                  user.id === selectedUser.id ? { ...user, name: e.target.value } : user
                )
              );
            }}
            onBlur={(e) => persistSelectedUser({ name: e.target.value })}
          />
          <Input
            type={showResetPassword ? "text" : "password"}
            placeholder="Set new password (optional)"
            onBlur={(e) => {
              if (e.target.value.trim()) {
                persistSelectedUser({ password: e.target.value.trim() });
              }
              e.target.value = "";
            }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: "0.6vh" }}>
            <input
              type="checkbox"
              checked={showResetPassword}
              onChange={(e) => setShowResetPassword(e.target.checked)}
            />
            Show password
          </label>
          <Note>Existing saved passwords cannot be displayed because they are securely hashed.</Note>

          <label style={{ display: "flex", alignItems: "center", gap: "0.6vh" }}>
            <input
              type="checkbox"
              checked={selectedUser.isAdmin}
              disabled={selectedUser.isAdmin && !canDemoteSelectedAdmin()}
              onChange={(e) => {
                const nextIsAdmin = e.target.checked;
                if (!nextIsAdmin && !canDemoteSelectedAdmin()) {
                  return;
                }
                persistSelectedUser({
                  isAdmin: nextIsAdmin,
                  facilities: nextIsAdmin ? [] : selectedUser.facilities,
                });
              }}
            />
            Admin access (all facilities)
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: "0.6vh" }}>
            <input
              type="checkbox"
              checked={Boolean(selectedUser.canRefreshService || selectedUser.isAdmin)}
              disabled={selectedUser.isAdmin}
              onChange={(e) => {
                persistSelectedUser({ canRefreshService: e.target.checked });
              }}
            />
            Can see and use refresh service
          </label>
          {selectedUser.isAdmin && (
            <Note>Admins always have refresh service access.</Note>
          )}

          {selectedUser.isAdmin ? (
            <Note>Admins can view all facilities. Facility toggles are disabled.</Note>
          ) : (
            <>
              <h3 style={{ marginTop: "1.2vh" }}>Facility permissions</h3>
              <FacilityGrid>
                {facilities.map((facility) => (
                  <FacilityItem key={facility}>
                    <input
                      type="checkbox"
                      checked={selectedUser.facilities.includes(facility)}
                      onChange={() => toggleFacility(facility)}
                    />
                    <span>{facility}</span>
                  </FacilityItem>
                ))}
              </FacilityGrid>
              {facilities.length === 0 && <Note>No facilities loaded from server.</Note>}
            </>
          )}

          <div style={{ marginTop: "1.4vh" }}>
            <Button onClick={removeSelectedUser}>Delete user</Button>
          </div>
        </Card>
      )}
    </Page>
  );
};

export default AdminSettings;
